/**
 * Resources Panel — Implementation
 *
 * Shows the scene's **manifest**: every file the scene document names, which
 * is what a folder is (CONTENT_MODEL_SPEC §2.1). Two things feed it and they
 * answer different halves of the question:
 *
 * - the `ResourceManager`, which holds the bytes the session has — everything
 *   packed in an opened archive, and everything imported since;
 * - the live scene's references, which are what the *document* will name.
 *
 * They diverge for a folder scene opened from storage: its files resolve by
 * URL and stream straight into Babylon, so nothing is cached and the resource
 * store is empty even though the scene plainly has files. Listing only the
 * store would show that scene as having no resources at all. So a reference
 * with no local bytes is listed too, with the nodes that name it — and the
 * actions that need bytes (import, download, delete, publish as asset) simply
 * do nothing for it, which is what they already did on a cache miss.
 *
 * A library reference (§3.4) is listed by its asset id, taken from
 * `SceneObject.contentRef` — the id is in the URL for exactly this kind of
 * editor use, and reading it needs no fetch.
 *
 * A folder file is *stored* under its SHA-256 (`assets/<hash>.glb`), which is
 * not a name anyone can read. So the card shows `files[k].name` — the name the
 * file was imported under, held by `ResourceManager.getAssetName` (spec §2.1)
 * — and falls back to the stored name only when the folder remembers none.
 * The stored name stays on the tooltip, because it is what the document says.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { TraceViewerElement } from 'trace-viewer';
import { Renderer } from 'trace-viewer/behaviors';
import {
    DROPPABLE_ASSET_EXTENSIONS, SCRIPT_EXTENSIONS, DATA_EXTENSIONS,
    IMAGE_EXTENSIONS, getAssetCategory, toDotExtensions,
} from 'trace-viewer/scene';
import { EDITOR_SCENE_DOT_EXTENSIONS, EDITOR_SCENE_EXTENSIONS } from './scene-formats';
import { EDITOR_EVENTS, dispatchResourcesChanged } from './editor-events';
import { collectFolderFileSrcs, normalizeFolderUri } from './cms/scene-folder';
import { showSpinnerToast, showToast } from './toast';
import { getCatalogToken, getCatalogSelectedFolderId, LS_TEAM_ID_KEY, apiConfig, extensionToEntityType } from './catalog-shared';
import { generateAssetThumbnail, generateImageThumbnail, type ThumbnailImporter } from 'trace-viewer/editor';
import { EntityType, Teams, Assets } from 'trace-viewer/cms';
import { withContentStorage } from './cms/asset-writes';
import type { FolderTeamResponseDto } from 'trace-viewer/cms';

// ============================================================================
// Types
// ============================================================================

interface ResourceEntry {
    path: string;
    /** Folder-relative stored name (`scripts/foo.ts`) — the tree key, and the
     *  file name bytes travel under. A hash for a saved folder file. */
    displayName: string;
    /** What to show the user: `files[k].name` when the folder knows one. */
    label: string;
    size: number;
    ext: string;
    typeLabel: string;
    /** Blob URL for image thumbnails; undefined for non-image types. */
    thumbnailUrl: string | undefined;
    /** Names of the scene objects whose `src` names this file. */
    referencedBy: string[];
    /** Library asset id, for a reference into the catalog (spec §3.4). */
    assetId: string | undefined;
    /** Whether this session holds the bytes — gates the byte-dependent actions. */
    hasBytes: boolean;
}

/** A folder or file node for the browsable tree view. */
interface FolderContents {
    folders: string[];        // subfolder names at this level
    files: ResourceEntry[];   // files at this level
}

// ============================================================================
// Helpers
// ============================================================================

const getViewer = (): TraceViewerElement | null => document.querySelector('trace-viewer');

const UPLOAD_EXTENSIONS = [
    ...toDotExtensions(DROPPABLE_ASSET_EXTENSIONS),
    ...EDITOR_SCENE_DOT_EXTENSIONS,
    ...toDotExtensions(SCRIPT_EXTENSIONS),
    ...toDotExtensions(DATA_EXTENSIONS),
];
const ACCEPT_STRING = UPLOAD_EXTENSIONS.join(',');

const CATEGORY_LABELS: Record<string, string> = {
    model: 'Model', splat: 'Model', image: 'Image', video: 'Video',
    script: 'Script', data: 'JSON', scene: 'Scene',
};

function getTypeLabel(ext: string): string {
    // Scene formats first: the viewer's category lookup still predates `.glz`.
    if (EDITOR_SCENE_EXTENSIONS.has(ext)) return CATEGORY_LABELS.scene;
    return CATEGORY_LABELS[getAssetCategory(ext)] ?? (ext ? ext.toUpperCase() : 'File');
}

/**
 * Card subtitle: what the entry *is*. A library reference has no bytes here
 * and no meaningful extension — its id is the useful thing to show.
 */
function entrySubtitle(entry: ResourceEntry): string {
    if (entry.assetId) return `Library · ${entry.assetId.slice(0, 8)}`;
    if (!entry.hasBytes) return entry.ext ? `In folder · .${entry.ext}` : 'In folder';
    return `${formatSize(entry.size)} · .${entry.ext}`;
}

/** Card tooltip: the file, then the nodes that name it. */
function entryTooltip(entry: ResourceEntry): string {
    const lines = [entry.label];
    // The document names the file by its stored (hashed) uri, so show that too
    // whenever it is not what the label already says.
    if (entry.displayName !== entry.label) lines.push(entry.displayName);
    if (entry.assetId) lines.push(`Library asset ${entry.assetId}`);
    if (entry.referencedBy.length > 0) {
        lines.push(`Used by: ${entry.referencedBy.join(', ')}`);
    }
    return lines.join('\n');
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Which scene objects name each referenced file, keyed the way the resource
 * store keys it (`./assets/x` and `assets/x` are the same file).
 */
function collectSceneReferences(): Map<string, { nodes: string[]; assetId?: string }> {
    const refs = new Map<string, { nodes: string[]; assetId?: string }>();
    const traceScene = getViewer()?.traceScene;
    if (!traceScene) return refs;

    const note = (key: string, nodeName: string, assetId?: string): void => {
        const existing = refs.get(key);
        if (existing) {
            if (!existing.nodes.includes(nodeName)) existing.nodes.push(nodeName);
            existing.assetId ??= assetId;
        } else {
            refs.set(key, { nodes: [nodeName], assetId });
        }
    };

    for (const object of traceScene.getSceneObjects()) {
        // The node's own src: a folder file, a library reference (which is
        // where `contentRef` comes from), or foreign media.
        if (object.src) {
            note(normalizeFolderUri(object.src), object.name, object.contentRef?.assetId);
        }

        // Files a BEHAVIOR owns — a ParticleSystem texture, an Image src, a
        // state value naming a swap target. Found by the same structural walk
        // the exporter uses, so a behavior that grows a new asset field does
        // not silently stop being listed. Without this pass such a file is
        // invisible in a folder scene, where nothing holds its bytes.
        const data = object.getSceneObjectData();
        if (data) {
            for (const uri of collectFolderFileSrcs([data])) note(uri, object.name);
        }
    }
    return refs;
}

/** Display name for a manifest entry: the file name, or the reference's tail. */
function entryDisplayName(path: string): string {
    const withoutQuery = path.split(/[?#]/, 1)[0];
    const tail = withoutQuery.slice(withoutQuery.lastIndexOf('/') + 1);
    return tail || withoutQuery;
}

function loadResources(prevEntries: ResourceEntry[]): ResourceEntry[] {
    const viewer = getViewer();
    const rm = viewer?.traceScene?.resources;
    if (!rm) return [];

    // Build lookup of previous thumbnail URLs so we can reuse them
    const prevUrls = new Map<string, string>();
    for (const e of prevEntries) {
        if (e.thumbnailUrl) prevUrls.set(e.path, e.thumbnailUrl);
    }

    const references = collectSceneReferences();

    const entries = rm.getAssetEntries().map(([path, blob]) => {
        const displayName = path.replace(/^assets\//, '');
        const ext = (displayName.match(/\.([^.]+)$/)?.[1] ?? '').toLowerCase();
        const isImage = IMAGE_EXTENSIONS.has(ext);

        // Reuse existing blob URL if the path hasn't changed, otherwise create a new one
        let thumbnailUrl: string | undefined;
        if (isImage) {
            thumbnailUrl = prevUrls.get(path) ?? URL.createObjectURL(blob);
            prevUrls.delete(path); // mark as consumed
        }

        const reference = references.get(path);
        references.delete(path);
        return {
            path, displayName, label: rm.getAssetName(path) ?? displayName,
            size: blob.size, ext, typeLabel: getTypeLabel(ext), thumbnailUrl,
            referencedBy: reference?.nodes ?? [],
            assetId: reference?.assetId,
            hasBytes: true,
        };
    });

    // Whatever the scene names but this session does not hold: a folder scene's
    // files streamed straight from storage, a library reference, foreign media.
    for (const [path, reference] of references) {
        const displayName = entryDisplayName(path.replace(/^assets\//, ''));
        const ext = (displayName.match(/\.([^.]+)$/)?.[1] ?? '').toLowerCase();
        entries.push({
            path,
            displayName,
            label: rm.getAssetName(path) ?? displayName,
            size: 0,
            ext,
            typeLabel: reference.assetId ? 'Library' : getTypeLabel(ext),
            thumbnailUrl: undefined,
            referencedBy: reference.nodes,
            assetId: reference.assetId,
            hasBytes: false,
        });
    }

    entries.sort((a, b) => a.label.localeCompare(b.label));

    // Revoke any previous blob URLs that are no longer in use
    for (const url of prevUrls.values()) {
        URL.revokeObjectURL(url);
    }

    return entries;
}

/** Given a prefix (e.g. "" or "scripts/"), return the folders and files at that level. */
function getContentsAtPrefix(entries: ResourceEntry[], prefix: string): FolderContents {
    const folders = new Set<string>();
    const files: ResourceEntry[] = [];

    for (const entry of entries) {
        // displayName has the "assets/" prefix already stripped
        if (!entry.displayName.startsWith(prefix)) continue;
        const rest = entry.displayName.slice(prefix.length);
        const slashIdx = rest.indexOf('/');
        if (slashIdx >= 0) {
            // There's a subfolder
            folders.add(rest.slice(0, slashIdx));
        } else {
            // File at this level
            files.push(entry);
        }
    }

    return {
        folders: [...folders].sort((a, b) => a.localeCompare(b)),
        files,
    };
}

// ============================================================================
// Type Icons (inline SVGs)
// ============================================================================

const ICON_COLOR: React.CSSProperties = { color: 'var(--colorNeutralForeground4)' };

const ModelIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
        <path d="M21 16.5c0 .38-.21.71-.53.88l-7.9 4.44c-.16.12-.36.18-.57.18-.21 0-.41-.06-.57-.18l-7.9-4.44A.991.991 0 013 16.5v-9c0-.38.21-.71.53-.88l7.9-4.44c.16-.12.36-.18.57-.18.21 0 .41.06.57.18l7.9 4.44c.32.17.53.5.53.88v9zM12 4.15L5 8.09v7.82l7 3.94 7-3.94V8.09l-7-3.94z"/>
    </svg>
);

const ImageIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
    </svg>
);

const VideoIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
        <path d="M8 5v14l11-7z"/>
    </svg>
);

const ScriptIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
        <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
    </svg>
);

const SceneIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
        <path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"/>
    </svg>
);

const FileIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
    </svg>
);

const FolderIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={ICON_COLOR}>
        <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
    </svg>
);

function TypeIcon({ type, size }: { type: string; size?: number }) {
    switch (type) {
        case 'Model': return <ModelIcon size={size} />;
        case 'Image': return <ImageIcon size={size} />;
        case 'Video': return <VideoIcon size={size} />;
        case 'Script': return <ScriptIcon size={size} />;
        case 'Scene': return <SceneIcon size={size} />;
        default: return <FileIcon size={size} />;
    }
}

// ============================================================================
// Styles
// ============================================================================

const s = {
    container: {
        padding: '8px',
        color: 'var(--colorNeutralForeground1)',
        fontSize: '12px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    } as React.CSSProperties,
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '4px',
        flexShrink: 0,
    } as React.CSSProperties,
    title: {
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--colorNeutralForeground1)',
    } as React.CSSProperties,
    badge: {
        fontSize: '11px',
        color: 'var(--colorNeutralForeground3)',
        backgroundColor: 'var(--colorNeutralBackground3)',
        padding: '1px 6px',
        borderRadius: '8px',
    } as React.CSSProperties,
    searchInput: {
        padding: '5px 8px',
        backgroundColor: 'var(--colorNeutralBackground3)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: '3px',
        color: 'var(--colorNeutralForeground1)',
        fontSize: '12px',
        outline: 'none',
        width: '100%',
        marginBottom: '6px',
        flexShrink: 0,
    } as React.CSSProperties,
    grid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        overflowY: 'auto',
        flex: 1,
        alignContent: 'flex-start',
        paddingBottom: '8px',
    } as React.CSSProperties,
    card: {
        backgroundColor: 'var(--colorNeutralBackground3)',
        borderRadius: '4px',
        cursor: 'pointer',
        overflow: 'hidden',
        border: '2px solid transparent',
        transition: 'border-color 0.15s',
        flexShrink: 0,
        flex: '1 1 120px',
        maxWidth: '300px',
        minWidth: '120px',
    } as React.CSSProperties,
    cardImg: {
        width: '100%',
        aspectRatio: '1',
        objectFit: 'cover',
        display: 'block',
        backgroundColor: 'var(--colorNeutralBackground2)',
    } as React.CSSProperties,
    cardThumb: {
        width: '100%',
        aspectRatio: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--colorNeutralBackground2)',
    } as React.CSSProperties,
    cardLabel: {
        padding: '4px 6px',
        fontSize: '11px',
        color: 'var(--colorNeutralForeground2)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    } as React.CSSProperties,
    cardSize: {
        padding: '0 6px 4px',
        fontSize: '10px',
        color: 'var(--colorNeutralForeground4)',
    } as React.CSSProperties,
    spacer: {
        flex: '1 1 120px',
        maxWidth: '300px',
        minWidth: '120px',
        height: 0,
        padding: 0,
        margin: 0,
        overflow: 'hidden',
    } as React.CSSProperties,
    center: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '8px',
        height: '100%',
        color: 'var(--colorNeutralForeground4)',
        fontSize: '12px',
        textAlign: 'center',
        padding: '16px',
    } as React.CSSProperties,
    infoBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 8px',
        marginBottom: '6px',
        backgroundColor: 'var(--colorNeutralBackground3)',
        borderRadius: '4px',
        flexShrink: 0,
    } as React.CSSProperties,
    infoDetails: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
    } as React.CSSProperties,
    infoName: {
        fontSize: '12px',
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    } as React.CSSProperties,
    infoMeta: {
        fontSize: '10px',
        color: 'var(--colorNeutralForeground3)',
    } as React.CSSProperties,
    infoActions: {
        display: 'flex',
        gap: '4px',
        flexShrink: 0,
    } as React.CSSProperties,
    actionBtn: {
        padding: '3px 8px',
        fontSize: '11px',
        fontWeight: 600,
        color: 'var(--colorNeutralForegroundOnBrand)',
        backgroundColor: 'var(--colorBrandBackground)',
        border: 'none',
        borderRadius: '3px',
        cursor: 'pointer',
    } as React.CSSProperties,
    actionBtnSecondary: {
        padding: '3px 8px',
        fontSize: '11px',
        fontWeight: 600,
        color: 'var(--colorNeutralForeground1)',
        backgroundColor: 'var(--colorNeutralBackground1Hover)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: '3px',
        cursor: 'pointer',
    } as React.CSSProperties,
    actionBtnDanger: {
        padding: '3px 8px',
        fontSize: '11px',
        fontWeight: 600,
        color: 'var(--colorPaletteRedForeground1)',
        backgroundColor: 'var(--colorNeutralBackground1Hover)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: '3px',
        cursor: 'pointer',
    } as React.CSSProperties,
    footer: {
        display: 'flex',
        gap: '4px',
        paddingTop: '4px',
        flexShrink: 0,
        borderTop: '1px solid var(--colorNeutralStroke2)',
    } as React.CSSProperties,
    addButton: {
        flex: 1,
        padding: '5px 8px',
        fontSize: '11px',
        fontWeight: 600,
        color: 'var(--colorNeutralForeground1)',
        backgroundColor: 'var(--colorNeutralBackground3)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: '3px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
    } as React.CSSProperties,
    backButton: {
        background: 'none',
        border: 'none',
        color: 'var(--colorNeutralForeground3)',
        cursor: 'pointer',
        padding: '4px',
        fontSize: '14px',
        lineHeight: 1,
        borderRadius: '3px',
    } as React.CSSProperties,
    breadcrumb: {
        fontSize: '11px',
        color: 'var(--colorNeutralForeground3)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    } as React.CSSProperties,
    dropOverlay: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 120, 212, 0.15)',
        border: '2px dashed var(--colorBrandBackground)',
        borderRadius: '4px',
        color: 'var(--colorBrandBackground)',
        fontSize: '13px',
        fontWeight: 600,
        pointerEvents: 'none',
        zIndex: 10,
    } as React.CSSProperties,
    publishDropdown: {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '4px',
        width: 'min(260px, calc(100% - 16px))',
        backgroundColor: 'var(--colorNeutralBackground1)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: '4px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 20,
        padding: '4px 0',
    } as React.CSSProperties,
    publishDropdownHeader: {
        padding: '4px 8px 2px',
        fontSize: '10px',
        fontWeight: 600,
        color: 'var(--colorNeutralForeground3)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    } as React.CSSProperties,
    publishNameInput: {
        width: '100%',
        padding: '4px 8px',
        fontSize: '11px',
        color: 'var(--colorNeutralForeground1)',
        backgroundColor: 'var(--colorNeutralBackground3)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: '3px',
        outline: 'none',
        boxSizing: 'border-box',
    } as React.CSSProperties,
    publishNameSection: {
        padding: '4px 8px 6px',
        borderBottom: '1px solid var(--colorNeutralStroke2)',
        marginBottom: '2px',
    } as React.CSSProperties,
    publishFolderList: {
        maxHeight: '160px',
        overflowY: 'auto',
    } as React.CSSProperties,
    publishDropdownItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        width: '100%',
        padding: '5px 8px',
        fontSize: '11px',
        color: 'var(--colorNeutralForeground1)',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: 0,
        cursor: 'pointer',
        textAlign: 'left',
        overflow: 'hidden',
    } as React.CSSProperties,
    publishDropdownItemActive: {
        backgroundColor: 'var(--colorNeutralBackground3)',
        fontWeight: 600,
    } as React.CSSProperties,
};

// ============================================================================
// Resource Card
// ============================================================================

const ResourceCard: React.FC<{
    entry: ResourceEntry;
    selected: boolean;
    onClick: () => void;
}> = ({ entry, selected, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const borderColor = selected
        ? 'var(--colorBrandBackground)'
        : hovered
            ? 'var(--colorNeutralStroke1)'
            : 'transparent';

    return (
        <div
            style={{ ...s.card, borderColor }}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {entry.thumbnailUrl ? (
                <img src={entry.thumbnailUrl} alt={entry.label} style={s.cardImg} crossOrigin="anonymous" />
            ) : (
                <div style={s.cardThumb}>
                    <TypeIcon type={entry.typeLabel} />
                </div>
            )}
            <div style={s.cardLabel} title={entryTooltip(entry)}>{entry.label}</div>
            <div style={s.cardSize}>{entrySubtitle(entry)}</div>
        </div>
    );
};

const FolderCard: React.FC<{
    name: string;
    onClick: () => void;
}> = ({ name, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            style={{ ...s.card, borderColor: hovered ? 'var(--colorBrandBackground)' : 'transparent' }}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div style={s.cardThumb}>
                <FolderIcon />
            </div>
            <div style={{ ...s.cardLabel, ...(hovered ? { color: 'var(--colorBrandBackground)' } : {}) }} title={name}>{name}/</div>
        </div>
    );
};

const Spacers = () => <>{Array.from({ length: 4 }, (_, i) => <div key={`sp-${i}`} style={s.spacer} />)}</>;

/** Folder row inside publish dropdown — handles its own hover state. */
const FolderRow: React.FC<{
    folder: FolderTeamResponseDto;
    isActive: boolean;
    onClick: () => void;
}> = ({ folder, isActive, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            style={{
                ...s.publishDropdownItem,
                ...(isActive ? s.publishDropdownItemActive : {}),
                ...(hovered && !isActive ? { backgroundColor: 'var(--colorNeutralBackground1Hover)' } : {}),
            }}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <FolderIcon size={14} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                {folder.title ?? 'Untitled'}
            </span>
        </button>
    );
};

// ============================================================================
// Main Component
// ============================================================================

export const ResourcesPanelImpl: React.FC = () => {
    const [assets, setAssets] = useState<ResourceEntry[]>([]);
    const [selectedPath, setSelectedPath] = useState<string | null>(null);
    const [query, setQuery] = useState('');
    const [dropping, setDropping] = useState(false);
    const [currentPrefix, setCurrentPrefix] = useState('');
    const dropCounterRef = useRef(0);

    // Refresh asset list from ResourceManager
    const refresh = useCallback(() => {
        setAssets(prev => loadResources(prev));
    }, []);

    // Initial load + event listeners
    useEffect(() => {
        refresh();
        const handler = () => refresh();
        window.addEventListener(EDITOR_EVENTS.RESOURCES_CHANGED, handler);
        window.addEventListener('trace-catalog:import', handler);
        window.addEventListener('trace-catalog:open', handler);

        // Poll ResourceManager.assetCount to catch missed events (e.g. timing
        // edge cases during scene open where the event fires before the
        // listener is registered, or when external code modifies assets).
        const lastKnownCount = { value: -1 };
        const poll = setInterval(() => {
            const rm = getViewer()?.traceScene?.resources;
            const count = rm?.assetCount ?? 0;
            if (count !== lastKnownCount.value) {
                lastKnownCount.value = count;
                refresh();
            }
        }, 500);

        return () => {
            window.removeEventListener(EDITOR_EVENTS.RESOURCES_CHANGED, handler);
            window.removeEventListener('trace-catalog:import', handler);
            window.removeEventListener('trace-catalog:open', handler);
            clearInterval(poll);
        };
    }, [refresh]);

    // Revoke all blob URLs on unmount
    useEffect(() => {
        return () => {
            for (const a of assets) {
                if (a.thumbnailUrl) URL.revokeObjectURL(a.thumbnailUrl);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When searching, show flat filtered results; otherwise show folder contents at currentPrefix
    const isSearching = query.length > 0;

    const filtered = useMemo(() => {
        if (!isSearching) return assets;
        const q = query.toLowerCase();
        return assets.filter(a =>
            a.displayName.toLowerCase().includes(q) ||
            a.typeLabel.toLowerCase().includes(q)
        );
    }, [assets, query, isSearching]);

    const folderContents = useMemo(
        () => isSearching ? null : getContentsAtPrefix(assets, currentPrefix),
        [assets, currentPrefix, isSearching]
    );

    // Selected asset details
    const selected = useMemo(
        () => selectedPath ? assets.find(a => a.path === selectedPath) ?? null : null,
        [assets, selectedPath]
    );

    // Reset prefix when scene changes (assets go empty then repopulate)
    const prevAssetCount = useRef(assets.length);
    useEffect(() => {
        if (prevAssetCount.current > 0 && assets.length === 0) {
            setCurrentPrefix('');
        }
        prevAssetCount.current = assets.length;
    }, [assets.length]);

    // ---- Actions ----

    const handleImport = useCallback(async (entry: ResourceEntry) => {
        const viewer = getViewer();
        const rm = viewer?.traceScene?.resources;
        if (!viewer?.import || !rm) return;

        const blob = rm.getAsset(entry.path);
        if (!blob) return;

        const dismiss = showSpinnerToast(`Importing ${entry.label}...`);
        try {
            // The object takes the human name; the blob keeps the stored one,
            // because that is the key `ResourceManager` holds the bytes under.
            const name = entry.label.replace(/\.[^/.]+$/, '');
            const { sceneObject } = await viewer.import(blob, entry.displayName, name);

            if (!EDITOR_SCENE_EXTENSIONS.has(entry.ext)) {
                const { min } = sceneObject.transform.getHierarchyBoundingVectors(true);
                sceneObject.transform.position.y -= min.y;
                if (!sceneObject.getBehavior(Renderer)) {
                    await sceneObject.addBehavior(Renderer);
                }
            }

            window.dispatchEvent(new CustomEvent('trace-catalog:import', { detail: { sceneObject } }));
            dismiss(`Imported ${sceneObject.name}`, 'success');
        } catch (err) {
            console.error('[Resources] Import failed:', err);
            dismiss('Import failed', 'error');
        }
    }, []);

    const handleDownload = useCallback((entry: ResourceEntry) => {
        const rm = getViewer()?.traceScene?.resources;
        if (!rm) return;
        const blob = rm.getAsset(entry.path);
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = entry.label;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, []);

    const handleDelete = useCallback((entry: ResourceEntry) => {
        const rm = getViewer()?.traceScene?.resources;
        if (!rm) return;

        rm.removeAsset(entry.path);
        dispatchResourcesChanged({ action: 'removed', path: entry.path });
        setSelectedPath(null);
        refresh();
    }, [refresh]);

    const handleAdd = useCallback(async (files: FileList | File[]) => {
        const viewer = getViewer();
        const rm = viewer?.traceScene?.resources;
        if (!rm) return;

        for (const file of files) {
            const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
            if (!ext || !UPLOAD_EXTENSIONS.includes(ext)) continue;

            // Place file in the currently browsed subfolder
            const assetPath = `assets/${currentPrefix}${file.name}`;

            if (ext === '.ts' && viewer.traceScene) {
                // Scripts always go in assets/scripts/ and register as behaviors
                const typeName = file.name.replace(/\.ts$/, '');
                const scriptPath = `./assets/scripts/${file.name}`;
                const source = await file.text();
                viewer.traceScene.registerScript(typeName, scriptPath, source);
            } else {
                await rm.addAssetWithDedup(assetPath, file);
            }
        }
        dispatchResourcesChanged({ action: 'added', path: null });
        refresh();
    }, [refresh, currentPrefix]);

    const handleAddClick = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = ACCEPT_STRING;
        input.onchange = async () => {
            if (input.files) await handleAdd(input.files);
        };
        input.click();
    }, [handleAdd]);

    // ---- Publish-to-folder flow ----

    const [publishEntry, setPublishEntry] = useState<ResourceEntry | null>(null);
    const [publishFolders, setPublishFolders] = useState<FolderTeamResponseDto[] | null>(null);
    const [publishLoading, setPublishLoading] = useState(false);
    const [publishTitle, setPublishTitle] = useState('');
    const publishRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        if (!publishEntry) return;
        const handler = (e: MouseEvent) => {
            if (publishRef.current && !publishRef.current.contains(e.target as Node)) {
                setPublishEntry(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [publishEntry]);

    const openPublishPicker = useCallback(async (entry: ResourceEntry) => {
        // Toggle off if already open for this entry
        if (publishEntry?.path === entry.path) {
            setPublishEntry(null);
            return;
        }

        const token = getCatalogToken();
        if (!token) {
            showToast('Sign in to the catalog first', 'error');
            return;
        }
        const teamId = localStorage.getItem(LS_TEAM_ID_KEY);
        if (!teamId) {
            showToast('No team selected', 'error');
            return;
        }

        setPublishEntry(entry);
        setPublishTitle(entry.label.replace(/\.[^/.]+$/, ''));
        setPublishFolders(null);
        setPublishLoading(true);

        try {
            const teamsClient = new Teams(apiConfig(token));
            const res = await teamsClient.foldersList(teamId);
            setPublishFolders(res.data?.folders ?? []);
        } catch (err) {
            console.error('[Resources] Failed to load folders:', err);
            showToast('Failed to load folders', 'error');
            setPublishEntry(null);
        } finally {
            setPublishLoading(false);
        }
    }, [publishEntry]);

    const doPublish = useCallback(async (entry: ResourceEntry, folder: FolderTeamResponseDto, assetTitle: string) => {
        setPublishEntry(null);

        const token = getCatalogToken();
        const teamId = localStorage.getItem(LS_TEAM_ID_KEY);
        if (!token || !teamId || !folder.folderId) return;

        const rm = getViewer()?.traceScene?.resources;
        const blob = rm?.getAsset(entry.path);
        if (!blob) return;

        const title = assetTitle.trim() || entry.label.replace(/\.[^/.]+$/, '');
        const dismiss = showSpinnerToast(`Publishing "${title}" to ${folder.title ?? 'folder'}...`);

        try {
            // A library row is named for a person to find, so it goes up under
            // the human name — the hash names bytes, not assets.
            const file = new File([blob], entry.label, { type: blob.type || 'application/octet-stream' });
            const entityType = extensionToEntityType(entry.label) ?? EntityType.None;

            const isImage = IMAGE_EXTENSIONS.has(entry.ext);
            let thumbnail: File | undefined;
            if (isImage) {
                thumbnail = await generateImageThumbnail(blob);
            } else {
                const viewer = document.querySelector('trace-viewer');
                if (viewer?.import && viewer.traceScene && viewer.scene) {
                    const importer: ThumbnailImporter = {
                        import: (b: Blob, fn: string, n?: string) => viewer.import(b, fn, n),
                        deleteSceneObject: (so) => viewer.traceScene?.deleteSceneObject(so),
                    };
                    thumbnail = await generateAssetThumbnail(file, importer, viewer.scene);
                }
            }

            const assetsClient = new Assets(apiConfig(token));
            // A resource can be any extension the panel accepts, `.gltf`
            // included — `withContentStorage` forces `Folder` for a document
            // and declares the build's mode for everything else.
            await assetsClient.assetsCreate(withContentStorage({
                FolderId: folder.folderId,
                TeamId: teamId,
                File: file,
                Thumbnail: thumbnail,
                Title: title,
                EntityType: entityType,
            }));

            dismiss(`Published "${entry.label}"`, 'success');
            window.dispatchEvent(new CustomEvent('trace-catalog:folder-refresh'));
        } catch (err) {
            console.error('[Resources] Publish failed:', err);
            const msg = err instanceof Error ? err.message : String(err);
            dismiss(`Publish failed: ${msg}`, 'error');
        }
    }, []);

    // ---- Drag-and-drop ----

    const onDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        dropCounterRef.current++;
        setDropping(true);
    }, []);

    const onDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        dropCounterRef.current--;
        if (dropCounterRef.current <= 0) {
            dropCounterRef.current = 0;
            setDropping(false);
        }
    }, []);

    const onDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []);

    const onDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        dropCounterRef.current = 0;
        setDropping(false);
        if (e.dataTransfer.files.length > 0) {
            await handleAdd(e.dataTransfer.files);
        }
    }, [handleAdd]);

    // ---- Render ----

    return (
        <div
            style={{ ...s.container, position: 'relative' }}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
        >
            {/* Header */}
            <div style={s.header}>
                {currentPrefix && !isSearching && (
                    <button
                        style={s.backButton}
                        onClick={() => {
                            // Go up one level: "scripts/" → "", "foo/bar/" → "foo/"
                            const parts = currentPrefix.slice(0, -1).split('/');
                            parts.pop();
                            setCurrentPrefix(parts.length > 0 ? parts.join('/') + '/' : '');
                            setSelectedPath(null);
                        }}
                        title="Back"
                    >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path fillRule="evenodd" d="M11.354 1.646a.5.5 0 010 .708L5.707 8l5.647 5.646a.5.5 0 01-.708.708l-6-6a.5.5 0 010-.708l6-6a.5.5 0 01.708 0z"/></svg>
                    </button>
                )}
                <span style={s.title}>{currentPrefix && !isSearching ? currentPrefix.slice(0, -1).split('/').pop() : 'Resources'}</span>
                {assets.length > 0 && <span style={s.badge}>{assets.length}</span>}
            </div>
            {currentPrefix && !isSearching && (
                <div style={s.breadcrumb}>assets/{currentPrefix}</div>
            )}

            {/* Search */}
            {assets.length > 0 && (
                <input
                    style={s.searchInput}
                    type="text"
                    placeholder="Search resources..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
            )}

            {/* Selected resource info bar */}
            {selected && (
                <div style={{ ...s.infoBar, position: 'relative' }} ref={publishRef}>
                    <TypeIcon type={selected.typeLabel} size={16} />
                    <div style={s.infoDetails}>
                        <div style={s.infoName} title={entryTooltip(selected)}>{selected.label}</div>
                        <div style={s.infoMeta}>{formatSize(selected.size)} &middot; .{selected.ext}</div>
                    </div>
                    <div style={s.infoActions}>
                        <button style={s.actionBtn} onClick={() => handleImport(selected)} title="Import into scene">Import</button>
                        <button style={s.actionBtnSecondary} onClick={() => openPublishPicker(selected)} title="Publish to catalog folder">
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z"/><path d="M8 11a.5.5 0 00.5-.5V2.707l2.146 2.147a.5.5 0 00.708-.708l-3-3a.5.5 0 00-.708 0l-3 3a.5.5 0 10.708.708L7.5 2.707V10.5a.5.5 0 00.5.5z"/></svg>
                        </button>
                        <button style={s.actionBtnSecondary} onClick={() => handleDownload(selected)} title="Download file">
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z"/><path d="M7.646 11.854a.5.5 0 00.708 0l3-3a.5.5 0 00-.708-.708L8.5 10.293V1.5a.5.5 0 00-1 0v8.793L5.354 8.146a.5.5 0 10-.708.708l3 3z"/></svg>
                        </button>
                        <button style={s.actionBtnDanger} onClick={() => handleDelete(selected)} title="Delete resource">
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"/><path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H6a1 1 0 011-1h2a1 1 0 011 1h3.5a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/></svg>
                        </button>
                    </div>
                    {publishEntry?.path === selected.path && (
                        <div style={s.publishDropdown}>
                            <div style={s.publishNameSection}>
                                <div style={s.publishDropdownHeader}>Name</div>
                                <input
                                    style={s.publishNameInput}
                                    type="text"
                                    value={publishTitle}
                                    onChange={e => setPublishTitle(e.target.value)}
                                    autoFocus
                                    onKeyDown={e => e.stopPropagation()}
                                />
                            </div>
                            <div style={s.publishDropdownHeader}>Folder</div>
                            <div style={s.publishFolderList}>
                                {publishLoading ? (
                                    <div style={s.publishDropdownItem}>Loading...</div>
                                ) : publishFolders?.length === 0 ? (
                                    <div style={s.publishDropdownItem}>No folders</div>
                                ) : publishFolders?.map(f => (
                                    <FolderRow
                                        key={f.folderId}
                                        folder={f}
                                        isActive={f.folderId === getCatalogSelectedFolderId()}
                                        onClick={() => doPublish(selected, f, publishTitle)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Content */}
            {assets.length === 0 ? (
                <div style={s.center}>
                    <FileIcon size={32} />
                    <div>No resources in scene</div>
                    <div style={{ fontSize: '11px', color: 'var(--colorNeutralForeground4)' }}>
                        Open a .trace scene or drop files here to add resources
                    </div>
                </div>
            ) : isSearching ? (
                filtered.length === 0 ? (
                    <div style={s.center}>No matches</div>
                ) : (
                    <div style={s.grid}>
                        {filtered.map(entry => (
                            <ResourceCard
                                key={entry.path}
                                entry={entry}
                                selected={entry.path === selectedPath}
                                onClick={() => setSelectedPath(prev => prev === entry.path ? null : entry.path)}
                            />
                        ))}
                        <Spacers />
                    </div>
                )
            ) : folderContents && (folderContents.folders.length > 0 || folderContents.files.length > 0) ? (
                <div style={s.grid}>
                    {folderContents.folders.map(folder => (
                        <FolderCard
                            key={`folder:${folder}`}
                            name={folder}
                            onClick={() => {
                                setCurrentPrefix(currentPrefix + folder + '/');
                                setSelectedPath(null);
                            }}
                        />
                    ))}
                    {folderContents.files.map(entry => (
                        <ResourceCard
                            key={entry.path}
                            entry={entry}
                            selected={entry.path === selectedPath}
                            onClick={() => setSelectedPath(prev => prev === entry.path ? null : entry.path)}
                        />
                    ))}
                    <Spacers />
                </div>
            ) : (
                <div style={s.center}>Empty folder</div>
            )}

            {/* Footer: Add button */}
            <div style={s.footer}>
                <button style={s.addButton} onClick={handleAddClick}>
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M8 3v10M3 8h10"/>
                    </svg>
                    Add Resource
                </button>
            </div>

            {/* Drop overlay */}
            {dropping && (
                <div style={s.dropOverlay}>
                    Drop files to add as resources
                </div>
            )}
        </div>
    );
};
